import { apiClient } from './client';
import type {
  ElmoQueueStatus,
  ElmoStarterApprovalResult,
  ElmoStarterDetail,
  ElmoStarterQueue,
  PersonWrite,
} from '@/types/oscar';

/**
 * Typed wrappers over PR-36's `/api/onboarding/elmo-starters*` endpoints — the
 * ELMO new-starter approval queue.
 *
 * Oscar reads the People Tracker's ELMO landing (`oscar.vElmoOnboarding`); it
 * never calls the ELMO API itself, because ELMO's 5,000/day limit is shared
 * across every credential. Reads are open to any Oscar role; approve/dismiss
 * are admin+helpdesk and undo is admin — all enforced server-side.
 */

/**
 * GET /api/onboarding/elmo-starters — the queue plus its provenance.
 *
 * Always read `SourceLooksStale` from the response before presenting the list:
 * the landing is refreshed by a manual sync in another tenant, so an empty
 * queue can mean "nothing loaded" rather than "no new starters".
 */
export function listElmoStarters(
  status: ElmoQueueStatus | 'all' = 'Pending',
  signal?: AbortSignal,
): Promise<ElmoStarterQueue> {
  return apiClient.get<ElmoStarterQueue>(
    `/api/onboarding/elmo-starters?status=${encodeURIComponent(status)}`,
    signal,
  );
}

/** GET /api/onboarding/elmo-starters/:elmoUserId — one record + the form prefill. */
export function getElmoStarter(elmoUserId: string, signal?: AbortSignal): Promise<ElmoStarterDetail> {
  return apiClient.get<ElmoStarterDetail>(
    `/api/onboarding/elmo-starters/${encodeURIComponent(elmoUserId)}`,
    signal,
  );
}

/**
 * POST /api/onboarding/elmo-starters/:elmoUserId/approve — creates the
 * `oscar.Person` and records the decision in one transaction. If the body
 * carries a DepartmentCode, that department's mandatory role software is
 * allocated in the same transaction.
 */
export function approveElmoStarter(elmoUserId: string, body: PersonWrite): Promise<ElmoStarterApprovalResult> {
  return apiClient.post<ElmoStarterApprovalResult>(
    `/api/onboarding/elmo-starters/${encodeURIComponent(elmoUserId)}/approve`,
    body,
  );
}

/** POST /api/onboarding/elmo-starters/:elmoUserId/dismiss — reason is mandatory. */
export function dismissElmoStarter(
  elmoUserId: string,
  reason: string,
): Promise<{ ElmoUserId: string; Status: string; Reason: string }> {
  return apiClient.post(
    `/api/onboarding/elmo-starters/${encodeURIComponent(elmoUserId)}/dismiss`,
    { Reason: reason },
  );
}

/**
 * POST /api/onboarding/elmo-starters/:elmoUserId/undo-dismissal — returns a
 * dismissed starter to the pending queue (admin). Only dismissals can be undone;
 * undoing an approval would orphan the person it created.
 */
export function undoElmoStarterDismissal(
  elmoUserId: string,
): Promise<{ ElmoUserId: string; Status: string }> {
  return apiClient.post(
    `/api/onboarding/elmo-starters/${encodeURIComponent(elmoUserId)}/undo-dismissal`,
  );
}
